import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// Read shape of ONE resolved recipient row from view__distribution_list
// (Phase 5 Usage / "Send Communication" — decision §2.15c). The backend
// reuses the Phase 0 resolve RPC verbatim, wraps its bare json array in
// {data:[...]}, and strict-validates every row. first/last name + email are
// nullable: the RPC's COALESCE(email, email_other, email_private) and the
// contact-name columns can all be null. A null-email row is still returned
// (the screen shows it "excluded — No email address", §2.15d), so it must
// validate, not be dropped.
export const DistributionListRecipientSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string().nullable(),
    contacts__id: z.string(),
    contacts__first_name: z.string().nullable(),
    contacts__last_name: z.string().nullable(),
    contacts__email: z.string().nullable(),
    // Email Phase 3 / Decisions §8 — to/cc role per addressee. Sourced from
    // public.contact_account_topics.role for the topic-subscribed path;
    // defaults to 'to' for force-include and added-account-without-topic
    // paths. Nullable + optional because:
    //   - .nullable() — Python zon validator (add_nullables=True) materializes
    //     missing optional fields as JSON null, which a bare .optional() Zod
    //     rejects (Phase 2 gotcha #1).
    //   - .optional() — older clients with cached responses pre-Phase 3 may
    //     still receive payloads without the key; better degrade gracefully.
    role: z.string().nullable().optional()
  })
  .strict()

export const DistributionListRecipientFields = makeFieldKeys(DistributionListRecipientSchema)

export type DistributionListRecipient = z.infer<typeof DistributionListRecipientSchema>

export function validateDistributionListRecipient(data: unknown): DistributionListRecipient {
  try {
    return validateWithZodSchema(
      DistributionListRecipientSchema,
      data,
      'distribution list recipient model'
    )
  } catch (error) {
    throw new Error(`Distribution list recipient model validation failed: ${error}`)
  }
}
